/**
 * The sub-category chips (§B chips row, §E.4, §E.9, §H.2).
 *
 * "All" first (the whole group), then the group's categories in order, each a
 * glass Chip with dot · label · count. One toolbar tab stop with roving focus:
 * ←/→ move focus (wrapping), Home/End jump, Enter/Space press.
 *
 * The row scrolls horizontally, bleeding to the panel edges, with a 24 px fade
 * on whichever side can still scroll (a data attribute written straight to the
 * DOM on scroll, no React state). A vertical wheel scrolls it sideways. The
 * pressed chip glides into view over 420 settle on the ticker (instant under
 * reduced motion). On touch layouts the row carries a transparent hit
 * extension above and below: a tap there presses the chip in that column.
 */
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type JSX, type KeyboardEvent, type MouseEvent } from "react";
import { Chip, DUR, EASE } from "@/design";
import { tween } from "@/lib/ticker";
import type { Category } from "@/contracts/data";
import { chipAtX, chipLabel, COPY, rovingIndex, scrollEdges, scrollTarget } from "./format";

export interface ChipsProps {
  categories: ReadonlyArray<Category & { count: number }>;
  /** Pressed category (null = All). */
  value: string | null;
  groupLabel: string;
  /** Places in the whole group (the "All" count). */
  total: number;
  onPress: (id: string | null) => void;
  reduced: boolean;
  /** Data not loaded yet: quiet placeholder pills. */
  loading?: boolean;
}

interface Item {
  id: string | null;
  label: string;
  count: number;
}

const EDGE_PAD = 20;

export function Chips({ categories, value, groupLabel, total, onPress, reduced, loading }: ChipsProps): JSX.Element {
  const rowRef = useRef<HTMLDivElement>(null);
  const items = useMemo<Item[]>(() => [{ id: null, label: COPY.all, count: total }, ...categories.map((c) => ({ id: c.id, label: c.label, count: c.count }))], [categories, total]);
  const pressedIndex = Math.max(0, items.findIndex((it) => it.id === value));

  // Roving tab stop: follows the pressed chip whenever the pressed chip or the group changes.
  const [focusIndex, setFocusIndex] = useState(pressedIndex);
  const [lastSync, setLastSync] = useState<{ items: Item[]; pressed: number }>({ items, pressed: pressedIndex });
  if (lastSync.items !== items || lastSync.pressed !== pressedIndex) {
    setLastSync({ items, pressed: pressedIndex });
    setFocusIndex(pressedIndex);
  }
  const tabIndex = Math.min(focusIndex, items.length - 1);

  /* ── edges ─────────────────────────────────────────────────────────── */
  const updateEdges = useCallback(() => {
    const el = rowRef.current;
    if (!el) return;
    const e = scrollEdges(el.scrollLeft, el.clientWidth, el.scrollWidth);
    el.toggleAttribute("data-fade-start", e.start);
    el.toggleAttribute("data-fade-end", e.end);
  }, []);

  useLayoutEffect(() => {
    updateEdges();
    const el = rowRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(updateEdges);
    ro.observe(el);
    return () => ro.disconnect();
  }, [updateEdges, items]);

  /* ── smooth scroll on the ticker ───────────────────────────────────── */
  const scrollAbort = useRef<AbortController | null>(null);
  const bringIntoView = useCallback(
    (index: number, animate: boolean) => {
      const el = rowRef.current;
      const chip = el?.querySelector<HTMLElement>(`[data-chip="${index}"]`);
      if (!el || !chip) return;
      const to = scrollTarget(chip.offsetLeft, chip.offsetWidth, el.scrollLeft, el.clientWidth, EDGE_PAD, EDGE_PAD, el.scrollWidth - el.clientWidth);
      scrollAbort.current?.abort();
      if (to == null) return;
      if (!animate) {
        el.scrollLeft = to;
        updateEdges();
        return;
      }
      const ac = new AbortController();
      scrollAbort.current = ac;
      const from = el.scrollLeft;
      void tween(DUR.calm, EASE.settle, (e) => {
        el.scrollLeft = from + (to - from) * e;
      }, { signal: ac.signal });
    },
    [updateEdges],
  );

  // The pressed chip scrolls into view (420 settle; at once on first paint and under reduced motion).
  const firstScroll = useRef(true);
  useLayoutEffect(() => {
    if (loading) return;
    bringIntoView(pressedIndex, !firstScroll.current && !reduced);
    firstScroll.current = false;
  }, [pressedIndex, items, reduced, loading, bringIntoView]);

  useEffect(() => () => scrollAbort.current?.abort(), []);

  // A vertical wheel scrolls the row sideways (needs a non-passive listener).
  useEffect(() => {
    const el = rowRef.current;
    if (!el) return undefined;
    const onWheel = (e: WheelEvent) => {
      if (el.scrollWidth <= el.clientWidth + 1) return;
      if (Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return;
      e.preventDefault();
      scrollAbort.current?.abort();
      el.scrollLeft += e.deltaMode === 1 ? e.deltaY * 16 : e.deltaY;
    };
    const stop = () => scrollAbort.current?.abort();
    el.addEventListener("wheel", onWheel, { passive: false });
    el.addEventListener("pointerdown", stop, { passive: true });
    return () => {
      el.removeEventListener("wheel", onWheel);
      el.removeEventListener("pointerdown", stop);
    };
  }, []);

  /* ── keyboard ──────────────────────────────────────────────────────── */
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const next = rovingIndex(tabIndex, e.key, items.length);
    if (next == null) return;
    e.preventDefault();
    setFocusIndex(next);
    rowRef.current?.querySelector<HTMLElement>(`[data-chip="${next}"]`)?.focus({ preventScroll: true });
    bringIntoView(next, !reduced);
  };

  // Transparent hit extension: a tap in the row's padding presses the chip in that column.
  const onRowClick = (e: MouseEvent<HTMLDivElement>) => {
    const el = rowRef.current;
    if (!el || e.target !== el) return;
    const chips = Array.from(el.querySelectorAll<HTMLElement>("[data-chip]"));
    const i = chipAtX(
      chips.map((c) => {
        const r = c.getBoundingClientRect();
        return { left: r.left, right: r.right };
      }),
      e.clientX,
    );
    if (i >= 0) chips[i].click();
  };

  if (loading) {
    return (
      <div className="ld-loc-panel-chipsbox" aria-hidden="true">
        <div className="ld-loc-panel-chips is-loading">
          {[44, 92, 104, 128].map((w, i) => (
            <i key={i} className="ld-loc-panel-chipskel" style={{ width: w }} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="ld-loc-panel-chipsbox">
      {/* The click handler only catches taps in the row's transparent padding (the hit
          extension); every chip is a real button in the toolbar, so keyboards never need it. */}
      <div
        ref={rowRef}
        className="ld-loc-panel-chips"
        role="toolbar"
        aria-label={`Categories in ${groupLabel}`}
        onKeyDown={onKeyDown}
        onScroll={updateEdges}
        onClick={onRowClick}
      >
        {items.map((it, i) => (
          <Chip
            key={it.id ?? "all"}
            glass
            selected={it.id === value}
            count={it.count}
            aria-label={chipLabel(it.label, it.count)}
            tabIndex={i === tabIndex ? 0 : -1}
            data-chip={i}
            onFocus={() => setFocusIndex(i)}
            onClick={() => onPress(it.id)}
          >
            {it.label}
          </Chip>
        ))}
      </div>
    </div>
  );
}
